import {ArrowUpRightIcon, MarkGithubIcon, XIcon} from '@primer/octicons-react'
import {IconButton, NavList, Text} from '@primer/react'
import {useWindowSize} from '@primer/react-brand'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {COPY, LINK_ICONS} from './CopilotSidebar.data'
import Styles from './CopilotSidebar.module.css'

export interface CopilotSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export const defaultProps: Partial<CopilotSidebarProps> = {}

const CopilotSidebar = (props: CopilotSidebarProps) => {
  const initializedProps: CopilotSidebarProps = {...defaultProps, ...props}
  const {isOpen, onClose} = initializedProps

  const {width} = useWindowSize()
  const isMobileView = useMemo(() => (width || 0) < 1024, [width])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const isOpenRef = useRef<boolean>(isOpen)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const isMobileViewRef = useRef<boolean>(isMobileView)

  const onBackdropClick = useCallback(
    (event: PointerEvent) => {
      if (!wrapperRef.current || !isOpenRef.current) return

      const target = event.target as HTMLElement | null
      if (!target) return

      const isClickInside = wrapperRef.current.contains(target)
      if (!isClickInside) {
        event.preventDefault()
        event.stopPropagation()
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    isMobileViewRef.current = isMobileView
  }, [isMobileView])

  useEffect(() => {
    if (!isMobileView) return

    isOpenRef.current = isOpen
    document.body.style.overflow = isOpen ? 'hidden' : ''

    if (isOpen) {
      document.addEventListener('click', onBackdropClick, {capture: true})

      // Store previously focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      // Focus on the first tab
      const focusElement = wrapperRef.current?.querySelector<HTMLElement>(
        'button, [href], [tabindex]:not([tabindex="-1"]), [data-tab]',
      )
      focusElement?.focus()
      if (!focusElement) wrapperRef.current?.focus()
    } else {
      document.removeEventListener('click', onBackdropClick, {capture: true})

      // Restore focus to previously focused element
      previousFocusRef.current?.focus()
      previousFocusRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isOpenRef.current || !isMobileViewRef.current) return

      if (event.key === 'Escape') {
        onClose()
        return
      }

      // Focus trap
      if (event.key === 'Tab') {
        const focusableSelector = 'button, [href], [tabindex]:not([tabindex="-1"])'
        const focusableElements = Array.from(
          wrapperRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || [],
        ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null)

        if (!focusableElements.length) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement?.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement?.focus()
          }
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('click', onBackdropClick, {capture: true})
      window.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <aside
      className={`${Styles['CopilotSidebar']} ${isOpen ? Styles['CopilotSidebar--open'] : ''}`.trim()}
      inert={!isOpen ? true : undefined}
      ref={wrapperRef}
    >
      <div className={Styles['CopilotSidebar__header']}>
        <a className={Styles['CopilotSidebar__header-link']} href={COPY.logo.href} aria-label={COPY.a11y.logo}>
          <MarkGithubIcon size={24} />
          <Text weight="semibold" size="large">
            {COPY.logo.label}
          </Text>
        </a>
        <IconButton
          className={Styles['CopilotSidebar__header-button']}
          icon={XIcon}
          onClick={onClose}
          aria-label={COPY.a11y.close}
          variant="invisible"
        />
      </div>

      <NavList>
        {Object.entries(COPY.links).map(([key, link]) => {
          const typedKey = key as keyof typeof COPY.links
          const Icon = LINK_ICONS[typedKey]

          return (
            <NavList.Item key={typedKey} href={link.href} {...(link.isExternal && {target: '_blank'})}>
              <NavList.LeadingVisual>
                <Icon />
              </NavList.LeadingVisual>

              {link.label}

              {link.isExternal && (
                <NavList.TrailingVisual>
                  <ArrowUpRightIcon />
                </NavList.TrailingVisual>
              )}
            </NavList.Item>
          )
        })}
      </NavList>
    </aside>
  )
}

export default CopilotSidebar
